import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Package, Plus, Minus, Trash2 } from "lucide-react";
import { productImgUrl } from "@/lib/imageUtils";
import { useSEO } from "@/hooks/useSEO";
import { useCart } from "@/context/CartContext";
import SHOP from "@/config/shop";

export default function CartPage() {
  const { items, updateQuantity, removeItem, total, clearCart } = useCart();
  const navigate = useNavigate();
  const shippingCost = total >= 2000 ? 0 : 99;
  const totalWithShipping = total + shippingCost;
  const totalWithoutVat = total / 1.21;
  const vatAmount = total - totalWithoutVat;
  useSEO('Nakupni kosik', `Vas nakupni kosik na ${SHOP.domain}`);

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="empty-cart">
        <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Kosik je prazdny</h1>
        <p className="text-slate-500 mb-6">Zatim jste nepridali zadny produkt.</p>
        <Link to="/produkty" className="inline-block bg-[#004E98] text-white px-6 py-3 rounded font-medium hover:bg-[#003B73] transition-colors" data-testid="continue-shopping-btn">Pokracovat v nakupu</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10" data-testid="cart-page">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-6">Nakupni kosik</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-bold uppercase text-slate-500">
            <div className="col-span-6">Produkt</div>
            <div className="col-span-2 text-center">Cena</div>
            <div className="col-span-2 text-center">Mnozstvi</div>
            <div className="col-span-2 text-right">Celkem</div>
          </div>
          <div className="space-y-3">
            {items.map(item => {
              const lineTotal = item.price_vat * item.quantity;
              const lineTotalNoVat = item.price * item.quantity;
              return (
                <div key={item.id_product} className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-12 gap-4 items-center" data-testid={`cart-item-${item.id_product}`}>
                  <div className="col-span-12 sm:col-span-6 flex items-center gap-3">
                    <Link to={`/produkt/${item.id_product}`} className="w-16 h-16 bg-slate-50 border border-slate-200 rounded overflow-hidden flex-shrink-0">
                      <img src={productImgUrl(item, 'small')} alt="" className="w-full h-full object-contain p-1" loading="lazy" />
                    </Link>
                    <div className="min-w-0">
                      <Link to={`/produkt/${item.id_product}`} className="text-sm font-medium text-slate-800 hover:text-[#004E98] line-clamp-2" data-testid={`cart-item-name-${item.id_product}`}>{item.name}</Link>
                      {item.reference && <p className="text-xs text-slate-400">Kod: {item.reference}</p>}
                    </div>
                  </div>
                  <div className="col-span-4 sm:col-span-2 text-center">
                    <div className="text-sm font-bold text-slate-800">{item.price_vat?.toFixed(2)} Kc</div>
                    <div className="text-xs text-slate-400">{item.price?.toFixed(2)} bez DPH</div>
                  </div>
                  <div className="col-span-4 sm:col-span-2 flex items-center justify-center gap-1">
                    <button onClick={() => updateQuantity(item.id_product, item.quantity - 1)} className="w-8 h-8 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-50" data-testid={`decrease-qty-${item.id_product}`}><Minus className="w-3 h-3" /></button>
                    <span className="w-10 text-center text-sm font-medium" data-testid={`qty-${item.id_product}`}>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id_product, item.quantity + 1)} className="w-8 h-8 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-50" data-testid={`increase-qty-${item.id_product}`}><Plus className="w-3 h-3" /></button>
                  </div>
                  <div className="col-span-4 sm:col-span-2 text-right">
                    <div className="text-sm font-bold text-slate-800">{lineTotal.toFixed(2)} Kc</div>
                    <div className="text-xs text-slate-400">{lineTotalNoVat.toFixed(2)} bez DPH</div>
                    <button onClick={() => removeItem(item.id_product)} className="text-red-400 hover:text-red-600 mt-1" data-testid={`remove-${item.id_product}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-4">
            <h2 className="font-semibold text-slate-800 mb-4">Souhrn</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Zbozi s DPH:</span><span className="font-medium">{total.toFixed(2)} Kc</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Zbozi bez DPH:</span><span className="text-slate-400">{totalWithoutVat.toFixed(2)} Kc</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">DPH 21%:</span><span className="text-slate-400">{vatAmount.toFixed(2)} Kc</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Doprava:</span>
                <span>{shippingCost === 0 ? <span className="text-emerald-600 font-medium">Zdarma</span> : `${shippingCost} Kc`}</span>
              </div>
              {shippingCost > 0 && <p className="text-xs text-slate-400">Doprava zdarma od 2 000 Kc</p>}
            </div>
            <div className="border-t border-slate-200 mt-4 pt-4">
              <div className="flex justify-between text-lg font-bold text-[#004E98]"><span>Celkem:</span><span>{totalWithShipping.toFixed(2)} Kc</span></div>
            </div>
            <button onClick={() => navigate('/pokladna')} className="w-full h-12 bg-[#FF6B35] text-white rounded font-bold mt-4 flex items-center justify-center gap-2 hover:bg-[#E85D2A] transition-colors" data-testid="go-to-checkout-btn">
              <Package className="w-5 h-5" /> Pokracovat k pokladne
            </button>
            <button onClick={clearCart} className="w-full text-sm text-slate-500 hover:text-red-600 mt-2 py-2" data-testid="clear-cart-btn">Vyprazdnit kosik</button>
          </div>
        </div>
      </div>
    </div>
  );
}
